/**
 * PATH: packages/email-hub/src/feature/lib/signedAttachment.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-SIGNEDATTACHMENT-001
 * PURPOSE: Decide whether a compose attachment is a SIGNED PDF produced
 *          by the in-app "open → sign → reply" flow, so the compose
 *          attachment chip can stamp a leading "Signed" badge.
 * WHY:     The signer flow (PdfEditor.deriveSavedFilename) names the
 *          output `"<stem> (signed).pdf"`, but the compose chip
 *          truncates the filename (`max-w-[220px] truncate`) — so the
 *          "(signed)" suffix is clipped off-screen and the operator
 *          can't tell the attached contract is the countersigned one
 *          (operator 2026-06-07: "Once signed it should say Signed in
 *          front of the attachment name when replying"). Surfacing it
 *          as a leading badge is immune to the truncation.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────────┐   ┌────────────┐
 *   │ file.name    │ → │ ends with "(signed).pdf" │ → │ true/false │
 *   └──────────────┘   └─────────────────────────┘   └────────────┘
 * RELATED:
 *   - components/PdfEditor.tsx (deriveSavedFilename): the WRITER that
 *     produces the "(signed).pdf" name this reader keys off.
 *   - pages/ComposePage.tsx: renders the badge in the attachment strip.
 */

/** True when an attachment filename is a signed PDF produced by the
 *  in-app PDF signing flow. Keyed off the deterministic
 *  `"<stem> (signed).pdf"` suffix that `PdfEditor.deriveSavedFilename`
 *  emits (case-insensitive, tolerant of surrounding whitespace).
 *  Pure: name -> boolean. */
export function isSignedAttachmentName(name: string | null | undefined): boolean {
  return /\(signed\)\.pdf$/i.test((name || '').trim())
}

/** True when an attachment filename is an INITIALLED PDF — initials were
 *  placed but no full signature (`"<stem> (initialled).pdf"`). Kept
 *  distinct from `isSignedAttachmentName` so the compose chip can never
 *  present an initials-only document as "Signed" (operator 2026-06-11:
 *  "it showed it as signed when it was just the initials").
 *  Pure: name -> boolean. */
export function isInitialledAttachmentName(name: string | null | undefined): boolean {
  return /\(initialled\)\.pdf$/i.test((name || '').trim())
}
